/**
 * SiteLogoField — Owner Configuration Centre control for the per-site PDF
 * header logo. Delivers three things in one panel:
 *
 *   1. **Upload**  — drag/drop or click-to-pick a PNG/JPEG (≤ 2 MB).
 *   2. **AI Logo Assistant** — Auto-Clean (background removal via corner-
 *      sample punch-out) or Industrialize (retint to charcoal/orange).
 *      Both variants stream through the local `/api/ai/logo/process`
 *      endpoint so the heavy pixel work stays server-side.
 *   3. **Live preview** — shows the logo exactly as it'll render in the
 *      PDF header (dark charcoal band, 108pt tall, left-aligned).
 *
 * The resulting logo is stored as a `data:image/png;base64,…` string on
 * `cfg.pdf_header_logo` so the shared prod backend just persists a plain
 * string (no separate object-storage bucket needed for MVP).
 */
import { useRef, useState } from "react";
import axios from "axios";
import { UploadCloud, Sparkles, Loader2, Trash2, Wand2, Building2, Check, AlertTriangle } from "lucide-react";
import { apiError } from "@/lib/api";

const PORTAL_BACKEND = process.env.REACT_APP_BACKEND_URL;
const portalApi = axios.create({ baseURL: PORTAL_BACKEND });

const MAX_BYTES = 2 * 1024 * 1024; // 2 MB — plenty for a header wordmark.

export default function SiteLogoField({ value, onChange, siteCode }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [history, setHistory] = useState([]); // recent original for reverting

  const pick = () => inputRef.current?.click();

  const readFile = (file) => new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/^image\/(png|jpeg|jpg|webp)$/i.test(file.type)) {
      setMsg({ type: "err", text: "Upload a PNG, JPG or WEBP image." });
      return;
    }
    if (file.size > MAX_BYTES) {
      setMsg({ type: "err", text: "Image too large — keep it under 2 MB." });
      return;
    }
    setMsg(null);
    try {
      const dataUrl = await readFile(file);
      if (value) setHistory((h) => [value, ...h].slice(0, 3));
      onChange(dataUrl);
      setMsg({ type: "ok", text: "Uploaded — preview below shows how it renders in the PDF header." });
    } catch (ex) {
      setMsg({ type: "err", text: `Could not read image: ${ex.message || ex}` });
    }
  };

  const process = async (mode) => {
    if (!value) { setMsg({ type: "err", text: "Upload a logo first." }); return; }
    setBusy(true); setMsg(null);
    try {
      const { data } = await portalApi.post("/api/ai/logo/process", {
        image_data_url: value,
        mode,
      });
      if (data?.image_data_url) {
        setHistory((h) => [value, ...h].slice(0, 3));
        onChange(data.image_data_url);
        setMsg({
          type: "ok",
          text: mode === "clean"
            ? "Auto-Clean applied — background punched to transparent."
            : "Industrialize applied — retinted to the Pit Link palette.",
        });
      }
    } catch (ex) {
      setMsg({ type: "err", text: apiError(ex) });
    } finally { setBusy(false); }
  };

  const undo = () => {
    if (!history.length) return;
    const [prev, ...rest] = history;
    onChange(prev);
    setHistory(rest);
    setMsg({ type: "ok", text: "Reverted to previous version." });
  };

  const clear = () => {
    if (value) setHistory((h) => [value, ...h].slice(0, 3));
    onChange("");
    setMsg({ type: "ok", text: "Custom logo cleared — PDFs will use the standard Pit Link mark." });
  };

  return (
    <div className="panel p-5" data-testid="site-logo-field">
      <div className="text-[10px] uppercase tracking-widest text-muted mb-1 flex items-center gap-2">
        <Building2 className="w-3.5 h-3.5" /> White-Label · PDF Header Logo
      </div>
      <div className="text-sm font-black mb-1">Custom PDF Header Logo</div>
      <div className="text-[11px] text-muted mb-3">
        Optional. Every report generated for <span className="text-white font-mono-tight">{siteCode || "this site"}</span> will
        render this logo in the header. The Pit Link watermark still stamps every page.
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg,image/webp"
        className="hidden"
        onChange={onFile}
        data-testid="site-logo-file"
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <button
          type="button"
          onClick={pick}
          className="btn btn-primary"
          data-testid="site-logo-upload"
          disabled={busy}
        >
          <UploadCloud className="w-4 h-4" /> {value ? "Replace logo" : "Upload logo"}
        </button>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => process("clean")}
            disabled={!value || busy}
            className="btn btn-ghost flex-1"
            data-testid="site-logo-ai-clean"
            title="Remove background — punches the dominant corner colour to transparent."
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Auto-Clean
          </button>
          <button
            type="button"
            onClick={() => process("industrialize")}
            disabled={!value || busy}
            className="btn btn-ghost flex-1"
            data-testid="site-logo-ai-industrialize"
            title="Retint to the Pit Link charcoal + safety-orange palette."
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />} Industrialize
          </button>
        </div>
      </div>

      {msg && (
        <div
          className={`pill ${msg.type === "ok" ? "pill-green" : "pill-red"} !text-xs !py-2 !px-3 justify-start mt-3`}
          data-testid="site-logo-msg"
        >
          {msg.type === "ok" ? <Check className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />} {msg.text}
        </div>
      )}

      {/* Live preview — replicates the PDF header band exactly. */}
      <div className="mt-4">
        <div className="text-[10px] uppercase tracking-widest text-muted mb-2">Preview · PDF Header</div>
        <div
          className="rounded-lg overflow-hidden"
          style={{
            background: "linear-gradient(180deg, #0f0f11 0%, #1a1a1f 100%)",
            border: "1px solid rgba(255,255,255,0.08)",
          }}
          data-testid="site-logo-preview"
        >
          <div className="flex items-center gap-6 px-5 py-4" style={{ minHeight: 128 }}>
            {value ? (
              <img
                src={value}
                alt="Site logo preview"
                style={{ maxHeight: 96, maxWidth: 360, width: "auto", height: "auto", filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.4))" }}
              />
            ) : (
              <img
                src="/shiftlog-logo.png?v=pl2"
                alt="Default Pit Link logo"
                style={{ maxHeight: 80, maxWidth: 320, width: "auto", height: "auto", opacity: 0.85 }}
              />
            )}
            <div className="flex-1 border-l pl-4" style={{ borderColor: "rgba(255,255,255,0.10)" }}>
              <div className="text-[10px] uppercase tracking-widest text-muted">Sample header</div>
              <div className="font-display font-black text-white text-base">
                Asset Serviceability Report · DIG-12
              </div>
              <div className="text-[10px] text-muted mt-0.5">
                {siteCode || "SITE-CODE"} · Generated {new Date().toLocaleDateString()}
              </div>
            </div>
          </div>
          <div className="px-5 pb-2 text-[10px] text-muted flex items-center justify-between" style={{ borderTop: "1px solid rgba(255,255,255,0.06)", paddingTop: 8 }}>
            <span>{value ? "Using your custom logo" : "Using standard Pit Link logo (no custom uploaded)"}</span>
            <span className="uppercase tracking-widest">Watermark: Pit Link (always on)</span>
          </div>
        </div>
      </div>

      {(value || history.length > 0) && (
        <div className="mt-3 flex items-center gap-2">
          {history.length > 0 && (
            <button
              type="button"
              onClick={undo}
              className="btn btn-ghost btn-sm"
              disabled={busy}
              data-testid="site-logo-undo"
            >
              ← Undo
            </button>
          )}
          {value && (
            <button
              type="button"
              onClick={clear}
              className="btn btn-ghost btn-sm text-red-400 hover:text-red-300"
              disabled={busy}
              data-testid="site-logo-clear"
            >
              <Trash2 className="w-3.5 h-3.5" /> Remove
            </button>
          )}
        </div>
      )}
    </div>
  );
}
